"use client"

import { Info, ArrowRight } from "lucide-react"
import moment from "moment"
import server from "../../utils/serverConfig"
import model1 from "../../assests/model1.png"
import appConfig from "../../utils/appConfig";

const BespokeCard = ({ ele, handlePricingCardClick }) => {
  const onCardClick = () => {
    handlePricingCardClick(ele)
  }

  const isExpired = moment(ele?.subscription?.end_date).unix() * 1000 < new Date().getTime()

  // Helper function to determine pricing display
  const getPriceDisplay = () => {
    if (ele?.pricing?.monthly) return { price: ele.pricing.monthly, period: "/month" }
    if (ele?.pricing?.quarterly) return { price: ele.pricing.quarterly, period: "/quarter" }
    if (ele?.pricing?.halfYearly) return { price: ele.pricing.halfYearly, period: "/half-year" }
    if (ele?.pricing?.yearly) return { price: ele.pricing.yearly, period: "/year" }
    return { price: ele?.amount || "N/A", period: "" }
  }

  const { price, period } = getPriceDisplay()
  const months = ele?.duration ? Math.round(ele.duration / 30) : 0

  // Determine border color class based on subscription status
  const borderColorClass = ele?.subscription
    ? !isExpired
      ? "border-[#2E7D32]"
      : "border-[#BDBDBD]"
    : "border-blue-500"

  return (
    <div
      className={`flex flex-col cursor-pointer w-full sm:w-[95%] rounded-xl bg-white ${borderColorClass} border-2 transition-all duration-300 hover:shadow-lg h-full relative overflow-hidden group`}
      onClick={onCardClick}
    >
      {/* Subtle background accent */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-40"></div>

      {/* Subscription Status Banner */}
      {ele?.subscription && (
        <div
          className={`py-2 px-4 font-medium tracking-wide rounded-t-xl text-center text-sm uppercase w-full relative z-10 ${
            !isExpired ? "bg-[#2E7D32] text-white" : "bg-[#BDBDBD] text-black"
          }`}
        >
          <span className="font-semibold tracking-wider">{isExpired ? "SUBSCRIPTION EXPIRED" : "SUBSCRIBED"}</span>
          <span className="block font-normal text-xs mt-0.5">
            ({moment(ele.subscription.start_date).format("DD-MMM-YYYY")} to{" "}
            {moment(ele.subscription.end_date).format("DD-MMM-YYYY")})
          </span>
        </div>
      )}

      {/* Card Header with Name and Price */}
      <div className="flex flex-col px-5 pt-4 pb-2 flex-grow relative z-10">
        <div className="flex items-start mb-3 gap-3 w-full">
          <div className="relative">
            <div className="absolute inset-0 bg-blue-100 rounded-md"></div>
            <img
              className="w-12 h-12 object-cover rounded-md flex-shrink-0 relative z-10 p-0.5"
              src={ele?.image ? `${server.server.baseUrl}${ele?.image}` : model1}
              alt={ele?.name || "Plan"}
            />
          </div>
          <div className="flex flex-col w-full min-w-0">
            <h2 className="text-lg font-bold text-gray-900 capitalize truncate overflow-hidden">{ele?.name}</h2>
            <div className="text-sm text-gray-500 overflow-hidden text-ellipsis">
              {ele?.name && ele.name.length > 18 ? ele.name.substring(0, 18) + "..." : ele.name}
            </div>
            {months > 0 && (
              <span className="text-xs font-medium text-purple-600 bg-[#F3E9FC] px-2 py-0.5 rounded-full w-fit mt-1 border border-blue-100">
                {months} month{months > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Price Display - keeping original color */}
        <div className="flex items-baseline mb-3 py-0 sm:py-4">
          <span className="text-3xl font-bold text-gray-800 leading-none">₹{price}</span>
          {period && <span className="text-base text-gray-500 font-medium ml-1">{period}</span>}
        </div>
      </div>

      {/* Description Section */}
      <div className="px-5 py-3 sm:py-8 border-t border-gray-100 bg-gray-50/70 relative z-10">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
            {ele?.description || "No description available"}
          </p>
        </div>
      </div>

      {/* Action Button */}
      <div className="px-5 py-4 mt-auto relative z-10">
        {ele?.subscription ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handlePricingCardClick(ele)
            }}
            className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-all duration-200 text-white shadow-sm ${
              !isExpired ? "bg-[#2E7D32] hover:bg-[#1b5e20]" : "bg-[#BDBDBD] hover:bg-[#9E9E9E]"
            }`}
          >
            Renew Now
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handlePricingCardClick(ele)
            }}
            className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm text-white relative overflow-hidden transition-all duration-300 ${appConfig.colors.button} hover:-translate-y-0.5 shadow-md hover:shadow-blue-300/30 bg-[length:200%_100%] bg-[position:0%_0%] hover:bg-[position:100%_0%]`}
          >
            <span className="relative z-10 flex items-center justify-center">
              Invest Now
              <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </button>
        )}
      </div>

      {/* Decorative corner accent */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-blue-100 rounded-bl-full -mr-12 -mt-12 opacity-50"></div>
    </div>
  )
}

export default BespokeCard
